import { Amount, DonorInput as BankDonorInput } from '@itc-mod/bank/shared';
import { Category, CrateSize, LotAmount, Place, Quality, RecordId, SUP_CATS } from '@itc-mod/supply/shared';
import { z } from 'zod';

// Donations' shapes, used by the server and the browser (design §7.3, plan 2C
// Assumptions 2–6). Item quantities are in supply's base units: crates, kits and
// units counted, everything else in thousandths of an SCU (supply's quantity.ts).
// aUEC amounts are whole aUEC.

export { RecordId };

/** Display names by user id, for the people a response names. */
export const People = z.record(z.string(), z.string());
export type People = z.infer<typeof People>;

export const DonationKind = z.enum(['auec', 'items']);
export type DonationKind = z.infer<typeof DonationKind>;

/** R5: pledged, then handed over, then accepted or rejected; withdrawn before; reversed after. */
export const DonationStatus = z.enum(['pledged', 'handed_over', 'accepted', 'rejected', 'withdrawn', 'reversed']);
export type DonationStatus = z.infer<typeof DonationStatus>;

/** Still open: a receiver may accept or reject it, and its donors may change or withdraw it. */
export const OPEN_STATUSES: readonly DonationStatus[] = ['pledged', 'handed_over'];

/** R7: one donor's leaderboard credit. `none` until the donation is accepted. */
export const CreditState = z.enum(['none', 'credited', 'awaiting_donor', 'awaiting_second', 'disputed']);
export type CreditState = z.infer<typeof CreditState>;

/** A donor: a user, or a guest by name, with their own credit. */
export const Donor = z.object({
  userId: z.string().nullable(),
  name: z.string().nullable(),
  credit: CreditState,
  /** Hundredths of a point, fixed at acceptance (plan 4B V2). */
  value: z.number().int().optional(),
});
export type Donor = z.infer<typeof Donor>;

/** How a line is counted: as supply has its category (crates by count and size, materials in SCU, kits by count, others SCU or units). */
export const LineUnit = z.enum(['crates', 'scu', 'kits', 'units']);
export type LineUnit = z.infer<typeof LineUnit>;

/** The unit a category fixes, or null when the line chooses SCU or units. */
export function unitOfCategory(category: (typeof SUP_CATS)[number]): LineUnit | null {
  if (category === 'Crates') return 'crates';
  if (category === 'Materials') return 'scu';
  if (category === 'Kits') return 'kits';
  return null;
}

/** One item line of a donation or a delivery. */
export const Line = z.object({
  id: z.string(),
  name: z.string(),
  category: Category,
  unit: LineUnit,
  /** Crates: the crate's size in SCU. */
  crateSize: z.number().int().nullable(),
  /** Counted, or thousandths of an SCU. */
  quantity: z.number().int(),
  /** Materials: 0–1000, or null when unknown. */
  quality: z.number().int().nullable(),
  /** Materials. */
  refined: z.boolean(),
  /** The request line it counts towards (Chunk 4), or null. */
  requestLine: z.string().nullable(),
  /** The material it's priced by, on a line for a target (plan 4B). */
  material: z.string().nullish(),
});
export type Line = z.infer<typeof Line>;

/** Where a pickup stands (Assumption 6). */
export const PickupStage = z.enum(['pledged', 'scheduled', 'in_transit']);
export type PickupStage = z.infer<typeof PickupStage>;

/** The ship that hauls it: a supply ship, or a name typed in. */
export const PickupShip = z.union([z.object({ shipId: z.string() }), z.object({ name: z.string() })]);
export type PickupShip = z.infer<typeof PickupShip>;

/** Pickup logistics, shared by pledges and expected deliveries (Assumption 6). */
export const Pickup = z.object({
  from: z.string(),
  at: z.iso.datetime().nullable(),
  collector: z.string().nullable(),
  ship: PickupShip.nullable(),
  contact: z.string(),
  plan: z.string(),
  stage: PickupStage,
  /** What another module planned it as (an op's id). Opaque here. */
  op: z.string().nullable().default(null),
});
export type Pickup = z.infer<typeof Pickup>;

export const SettleStatus = z.enum(['none', 'pending', 'done', 'failed']);
export type SettleStatus = z.infer<typeof SettleStatus>;

/** What one line's undoing took back (R9): the amount, out of what was stocked. */
export const LineTaken = z.object({ lineId: z.string(), lotId: z.string().nullable(), stocked: z.number().int(), taken: z.number().int() });
export type LineTaken = z.infer<typeof LineTaken>;

/**
 * The deposit or stock that follows acceptance (`phase: settle`), and its undoing
 * after a reversal (`phase: undo`), as Donations' handlers record them (R10).
 */
export const Settlement = z.object({
  phase: z.enum(['settle', 'undo']),
  status: SettleStatus,
  /** The bank entry the deposit made. */
  entryId: z.string().nullable(),
  /** What the stock added, per lot. */
  stocked: z.array(LotAmount),
  /** Each line's lot, in line order (null for a line of 0). */
  lineLots: z.array(z.string().nullable()),
  /** The bank entry that reversed the deposit. */
  reversalEntryId: z.string().nullable(),
  /** What undoing the stock took back, per line. */
  taken: z.array(LineTaken),
  /** What couldn't be taken back: "Only 3 of 5 × 8 SCU RMC crates were still there". */
  short: z.array(z.string()),
  /** The callee's message for a lasting failure. */
  error: z.string(),
});
export type Settlement = z.infer<typeof Settlement>;

export const NO_SETTLEMENT: Settlement = { phase: 'settle', status: 'none', entryId: null, stocked: [], lineLots: [], reversalEntryId: null, taken: [], short: [], error: '' };

export const Donation = z.object({
  id: z.string(),
  kind: DonationKind,
  status: DonationStatus,
  donors: z.array(Donor),
  /** The donors' credit summed up (R7). */
  credit: CreditState,
  /** aUEC only. */
  amount: z.number().int().nullable(),
  /** aUEC: the bank account, or null for the default. */
  account: z.string().nullable(),
  lines: z.array(Line),
  /** Items: where they go, or null for the default. */
  place: Place.nullable(),
  /** What it's for (plan 4B), or null. */
  target: z.string().nullable(),
  request: z.string().nullable(),
  /** aUEC: the request line it counts towards. Item lines link one by one. */
  requestLine: z.string().nullable(),
  pickup: Pickup.nullable(),
  notes: z.string(),
  recordedBy: z.string(),
  acceptedBy: z.string().nullable(),
  acceptedAt: z.iso.datetime().nullable(),
  /** Who rejected or reversed it, and why. */
  decidedBy: z.string().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  reason: z.string(),
  settlement: Settlement,
  /** When it was accepted, or recorded if never accepted (Assumption 4). */
  date: z.iso.datetime(),
  created: z.iso.datetime(),
  version: z.number().int(),
});
export type Donation = z.infer<typeof Donation>;

/** What the viewer may do to a donation now (R5–R7), so the page shows only those buttons. */
export const DonationActions = z.object({
  handOver: z.boolean(),
  edit: z.boolean(),
  withdraw: z.boolean(),
  accept: z.boolean(),
  reject: z.boolean(),
  confirm: z.boolean(),
  dispute: z.boolean(),
  approveCredit: z.boolean(),
  reverse: z.boolean(),
  retry: z.boolean(),
});
export type DonationActions = z.infer<typeof DonationActions>;

// Inputs.

/** A donor as entered: a member, or a guest's name (up to 64 characters). */
export const DonorInput = z.union([z.object({ userId: RecordId }).strict(), z.object({ name: z.string().trim().min(1).max(64) }).strict()]);
export type DonorInput = z.infer<typeof DonorInput>;

/** One item line as entered. The unit follows from the category, or is SCU or units. */
export const LineInput = z
  .object({
    id: z.string().max(32).nullable().default(null),
    name: z.string().trim().min(1).max(80),
    category: Category,
    unit: z.enum(['scu', 'units']).default('units'),
    crateSize: CrateSize.nullable().default(null),
    quantity: z.number().int().min(0).max(1e12),
    quality: Quality.default(null),
    refined: z.boolean().default(false),
    requestLine: z.string().max(32).nullable().default(null),
    /** A target's line: the material it's priced by. */
    material: RecordId.nullable().default(null),
  })
  .transform((line) => ({ ...line, unit: unitOfCategory(line.category) ?? line.unit }));
export type LineInput = z.input<typeof LineInput>;

const Text = (max: number) => z.string().trim().max(max).default('');

export const PickupInput = z.object({
  from: Text(100),
  at: z.iso.datetime().nullable().default(null),
  collector: RecordId.nullable().default(null),
  ship: z.union([z.object({ shipId: RecordId }).strict(), z.object({ name: z.string().trim().min(1).max(64) }).strict()]).nullable().default(null),
  contact: Text(100),
  plan: Text(500),
  stage: PickupStage.default('pledged'),
});
export type PickupInput = z.input<typeof PickupInput>;

/** What a donation is, as a pledge, a record or an edit enters it. */
const Gift = z.object({
  kind: DonationKind,
  /** aUEC only: whole aUEC. */
  amount: Amount.nullable().default(null),
  /** aUEC only: the bank account, or null for the default. */
  account: RecordId.nullable().default(null),
  /** Items only: up to 30 lines, so stocking fits one batch. */
  lines: z.array(LineInput).max(30).default([]),
  /** Items only: where they go, or null for the default. */
  place: Place.nullable().default(null),
  /** The request it's for (R2: an Active one), or null. */
  request: RecordId.nullable().default(null),
  /** aUEC only: the request line it counts towards. */
  requestLine: z.string().max(32).nullable().default(null),
  pickup: PickupInput.nullable().default(null),
  notes: Text(1000),
});

/** A pledge, or a donation recorded as yourself: Pledged, or Handed over. */
export const PledgeInput = Gift.extend({
  handedOver: z.boolean().default(false),
  /** What it's for (plan 4B T2), or null. */
  target: RecordId.nullable().default(null),
  /** On a target, who gives with the pledger. */
  contributors: z.array(DonorInput).max(9).default([]),
});
export type PledgeInput = z.input<typeof PledgeInput>;

/** A donation a receiver records for others: Pledged, Handed over, or accepted at once. */
export const RecordInput = Gift.extend({
  donors: z.array(DonorInput).min(1).max(10),
  handedOver: z.boolean().default(false),
  acceptNow: z.boolean().default(false),
  target: RecordId.nullable().default(null),
});
export type RecordInput = z.input<typeof RecordInput>;

/** An edit of an open donation, compare-and-set on its version. */
export const UpdateInput = Gift.omit({ kind: true }).extend({ version: z.number().int().min(1) });
export type UpdateInput = z.input<typeof UpdateInput>;

/** Accepting: the receiver may set the account or the place, and correct the lines. */
export const AcceptInput = z.object({
  version: z.number().int().min(1),
  account: RecordId.nullable().default(null),
  place: Place.nullable().default(null),
  lines: z.array(LineInput).max(30).nullable().default(null),
  /** "Mark the request fulfilled" (R4): a receiver who is also a coordinator. */
  fulfilRequest: z.boolean().default(false),
  /** Hundredths of a point: a receiver's own total, replacing the lines' (D17). */
  value: z.number().int().min(0).max(1e13).nullable().default(null),
});
export type AcceptInput = z.input<typeof AcceptInput>;

export const ReasonInput = z.object({ version: z.number().int().min(1), reason: z.string().trim().min(1).max(500) });
export const VersionInput = z.object({ version: z.number().int().min(1) });

/** A coordinator sets an open donation's pickup logistics (Incoming). */
export const PickupUpdate = z.object({ version: z.number().int().min(1), pickup: PickupInput.nullable() });

export const Preferences = z.object({ hideFromBoards: z.boolean() });
export type Preferences = z.infer<typeof Preferences>;

/** The bank's donor field for a deposit: a member, or a name. */
export type BankDonor = z.input<typeof BankDonorInput>;
